import { LineLogin } from '@play-co/native-game-js/modules/LineLogin';
import { assertNever } from 'src/replicant/utils';
import { captureGenericError } from '../sentry';
import { getLineConfig } from 'src/conf/env';
import { GCInstant } from '../gcinstant';

const { channelId, scopes } = getLineConfig(GCInstant.legacyNative);

/** Params for LineLogin.getInstance()?.login(lineLoginParams) */
const lineLoginParams = {
  permissions: scopes,
};

/** Params for LineLogin.getInstance()?.setup(lineLoginSetupParams) */
const lineLoginSetupParams = {
  channelID: channelId,
  universalLinkURL:
    'https://buruburu-ap-northeast-1.line.gc-internal.net/prod/web',
};

/**
 * Check if user is authenticated with LINE.
 */
export async function checkLineAuthentication(): Promise<boolean> {
  const lineLogin = LineLogin.getInstance();

  // Module is not available, consider user not authenticated.
  if (!lineLogin) {
    return false;
  }

  // Result of the authentication check
  let result: boolean | undefined = undefined;

  while (result === undefined) {
    const status = await lineLogin.status();

    // Check the login status
    switch (status.type) {
      case 'uninitialized':
        await lineLogin.setup(lineLoginSetupParams);
        break;
      case 'initialized':
      case 'authenticating':
        await delay(300);
        break;
      case 'unauthenticated':
      case 'expired':
        result = false;
        break;
      case 'authenticated':
        result = true;
        break;
      default:
        assertNever(status, 'Unexpected login status');
    }
  }

  // Return the result of the authentication check - true if authenticated.
  return result;
}

/**
 * Attempt to login with LINE. It will timeout after 10 seconds.
 */
export async function attemptLineLogin() {
  // Get the login screen instance.
  const lineLogin = LineLogin.getInstance();
  if (!lineLogin) {
    return false;
  }

  // The return value - it will be true if the login was successful.
  let result: boolean | undefined = undefined;

  while (result === undefined) {
    const status = await lineLogin.status();

    // Check the login status
    switch (status.type) {
      case 'uninitialized':
        await lineLogin.setup(lineLoginSetupParams);
        break;
      case 'authenticating':
        await delay(300);
        break;
      case 'initialized':
      case 'unauthenticated':
      case 'expired':
      case 'authenticated':
        await lineLogin.login(lineLoginParams).then((response) => {
          // Only capture errors that aren't caused by the user cancelling the login
          if (response.type === 'failure' && !response.error.isUserCancelled) {
            captureError(`Login with LINE failed`, response.error);
          }
          result = response.type === 'success';
        });
        break;
      default:
        assertNever(status, 'Unexpected login status');
    }
  }

  return result;
}
/**
 * Wrapper for capturing errors, that will fallback to console.error if Sentry is not available.
 *
 * @param message
 * @param error
 */
function captureError(message: string, error: unknown) {
  try {
    captureGenericError(message, error);
  } catch {
    console.error(message, error);
  }
}

/**
 * Delay function to wait for a given amount of time.
 */
async function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
